import * as THREE from 'three';
import {
  addBeam,
  addMesh,
  createTrapezoidGeometry,
  finishModel,
  lowPolyMaterial,
  type VectorTuple,
} from './modelHelpers';

const timberSnow = lowPolyMaterial('timberline-snow', 0xf2f7f3, {
  emissive: 0xb8d2ce,
  emissiveIntensity: 0.07,
});
const timber = lowPolyMaterial('weathered-alpine-timber', 0x6b6358);
const timberDark = lowPolyMaterial('timber-shadow', 0x484c48);
const alpineGreen = lowPolyMaterial('krummholz-green', 0x356c5a, {
  emissive: 0x174737,
  emissiveIntensity: 0.12,
});
const alpineGreenLight = lowPolyMaterial('krummholz-new-growth', 0x568168);
const cairnStone = lowPolyMaterial('cairn-stone', 0x69797a);
const cairnShade = lowPolyMaterial('cairn-shadow-stone', 0x4f6062);
const ribbonCoral = lowPolyMaterial('ribbon-coral', 0xca554f, { side: THREE.DoubleSide });
const ribbonOchre = lowPolyMaterial('ribbon-ochre', 0xd99b45, { side: THREE.DoubleSide });
const steel = lowPolyMaterial('weathered-steel', 0x73888b, {
  roughness: 0.58,
  metalness: 0.42,
});
const steelShadow = lowPolyMaterial('weathered-steel-shadow', 0x35494d, {
  roughness: 0.62,
  metalness: 0.34,
  side: THREE.DoubleSide,
});
const packCanvas = lowPolyMaterial('ridge-pack-canvas', 0x52766f);
const packCanvasShadow = lowPolyMaterial('ridge-pack-shadow-canvas', 0x344e4c);
const ravenBlack = lowPolyMaterial('raven-black-feathers', 0x202a30, {
  emissive: 0x101a20,
  emissiveIntensity: 0.12,
  side: THREE.DoubleSide,
});
const ravenBlue = lowPolyMaterial('raven-blue-feathers', 0x3d5962, {
  emissive: 0x182c34,
  emissiveIntensity: 0.16,
});
const ravenBeak = lowPolyMaterial('raven-beak', 0x27363a);
const ravenEye = lowPolyMaterial('raven-eye', 0xe2a841, {
  emissive: 0xa95d1e,
  emissiveIntensity: 0.42,
});
const ptarmiganWhite = lowPolyMaterial('ptarmigan-winter-plumage', 0xe7e8df, {
  emissive: 0xaabfc0,
  emissiveIntensity: 0.05,
});
const ptarmiganShadow = lowPolyMaterial('ptarmigan-feather-shadow', 0xaababd);
const ptarmiganUnderwing = lowPolyMaterial('ptarmigan-underwing', 0x7f969a);
const ptarmiganCharcoal = lowPolyMaterial('ptarmigan-tail-and-beak', 0x273438, {
  emissive: 0x111b1e,
  emissiveIntensity: 0.08,
});
const ptarmiganComb = lowPolyMaterial('ptarmigan-eyebrow-comb', 0xc94e3d, {
  emissive: 0x7c241d,
  emissiveIntensity: 0.16,
});
const ptarmiganFeet = lowPolyMaterial('ptarmigan-feet', 0x6b6257);
const powderBathBlue = lowPolyMaterial('powder-bath-shadow', 0x8fb8c2, {
  emissive: 0x416f79,
  emissiveIntensity: 0.1,
});

/**
 * A tiny asymmetric, closed cushion for dense alpine needles. The broad
 * overlapping footprint stays solid from above while the raised ridge and
 * tapered lee tip preserve the wind-combed profile from the side.
 */
function createKrummholzNeedlePadGeometry(): THREE.BufferGeometry {
  const geometry = new THREE.IcosahedronGeometry(1, 0);
  const positions = geometry.getAttribute('position') as THREE.BufferAttribute;
  for (let index = 0; index < positions.count; index += 1) {
    const x = positions.getX(index);
    const y = positions.getY(index);
    const z = positions.getZ(index);
    // A little upward sweep toward the lee end makes a cushion, not a ball;
    // unequal front/back depth keeps its top footprint organic.
    const height = y * (0.76 + x * 0.045);
    positions.setXYZ(
      index,
      x + y * 0.1,
      height,
      z * (0.9 - x * 0.06) + y * 0.025,
    );
  }
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

export function createKrummholzModel(): THREE.Group {
  const group = new THREE.Group();
  // A connected chain of short, tapering sections reads as a trunk that has
  // repeatedly been bent and recovered, rather than one smooth leaning pole.
  const trunkPoints: readonly VectorTuple[] = [
    [-1.48, 0, 0.14],
    [-1.42, 0.72, -0.02],
    [-1.12, 1.4, -0.16],
    [-0.48, 1.97, 0.08],
    [0.36, 2.3, -0.12],
    [1.34, 2.4, 0.04],
    [2.45, 2.52, -0.14],
  ];
  const trunkRadii = [0.35, 0.31, 0.26, 0.21, 0.16, 0.11, 0.045] as const;
  for (let index = 0; index < trunkPoints.length - 1; index += 1) {
    addBeam(
      group,
      `krummholz-trunk-section-${index}`,
      trunkPoints[index]!,
      trunkPoints[index + 1]!,
      trunkRadii[index]!,
      timberDark,
      trunkRadii[index + 1]!,
      6,
    );
  }

  const branches = [
    { name: 'low', points: [[-1.12, 1.4, -0.16], [-0.25, 1.42, 0.33], [0.92, 1.23, 0.45]], radii: [0.17, 0.1, 0.035] },
    { name: 'crown', points: [[-0.48, 1.97, 0.08], [0.02, 2.7, -0.32], [0.94, 3.08, -0.48]], radii: [0.15, 0.09, 0.035] },
    { name: 'back', points: [[0.36, 2.3, -0.12], [0.98, 2.02, -0.48], [1.73, 1.9, -0.55]], radii: [0.12, 0.07, 0.03] },
  ] as const;
  branches.forEach((branch) => {
    for (let index = 0; index < branch.points.length - 1; index += 1) {
      addBeam(
        group,
        `krummholz-${branch.name}-branch-${index}`,
        branch.points[index]!,
        branch.points[index + 1]!,
        branch.radii[index]!,
        timberDark,
        branch.radii[index + 1]!,
        5,
      );
    }
  });

  // Faceted knuckles make every branch/trunk junction visibly continuous.
  for (const [index, position] of trunkPoints.slice(1, -1).entries()) {
    addMesh(group, `krummholz-trunk-knot-${index}`, new THREE.DodecahedronGeometry(0.19, 0), timberDark, {
      position,
      scale: 0.78 + index * 0.045,
      rotation: [index * 0.17, index * 0.31, -0.12],
    });
  }

  const pads: readonly {
    position: VectorTuple;
    scale: VectorTuple;
    rotation: number;
    light: boolean;
  }[] = [
    // A heavily overlapping main crown follows the bent trunk toward its
    // narrow lee tip; there is no repeated lobe-with-a-hole topology.
    { position: [-0.88, 1.87, 0.02], scale: [1.15, 0.86, 0.92], rotation: 0.08, light: false },
    { position: [0, 2.16, -0.04], scale: [1.3, 0.86, 0.98], rotation: -0.12, light: true },
    { position: [0.9, 2.4, -0.1], scale: [1.35, 0.78, 0.94], rotation: 0.13, light: false },
    { position: [1.8, 2.5, -0.12], scale: [1.25, 0.68, 0.86], rotation: -0.08, light: true },
    { position: [2.55, 2.5, -0.15], scale: [0.8, 0.52, 0.66], rotation: 0.1, light: false },
    // Crown, low branch, and back branch cushions stay connected to their
    // authored timber underneath while breaking up the main silhouette.
    { position: [0.15, 2.62, -0.26], scale: [0.9, 0.7, 0.78], rotation: -0.18, light: false },
    { position: [0.82, 2.9, -0.39], scale: [0.85, 0.54, 0.7], rotation: 0.16, light: true },
    { position: [0.2, 1.58, 0.25], scale: [1.15, 0.56, 0.82], rotation: -0.16, light: false },
    { position: [0.92, 1.47, 0.35], scale: [0.95, 0.48, 0.7], rotation: 0.06, light: false },
    { position: [1.04, 2.02, -0.34], scale: [1, 0.51, 0.74], rotation: -0.14, light: true },
    { position: [1.65, 1.92, -0.43], scale: [0.75, 0.4, 0.62], rotation: 0.12, light: false },
  ];
  const needlePadGeometry = createKrummholzNeedlePadGeometry();
  const padPitches = [0.04, -0.07, 0.06, -0.04, 0.08, -0.06, 0.1, 0.08, -0.05, 0.05, -0.08] as const;
  const padRolls = [-0.1, 0.07, -0.055, 0.085, -0.07, 0.11, -0.09, 0.09, -0.075, 0.065, -0.05] as const;
  pads.forEach((pad, index) => {
    const material = pad.light ? alpineGreenLight : alpineGreen;
    addMesh(group, `krummholz-needle-cushion-${index}`, needlePadGeometry, material, {
      position: pad.position,
      scale: pad.scale,
      rotation: [padPitches[index]!, pad.rotation, padRolls[index]!],
    });
  });

  // Small offset patches read as wind-stuck snow, leaving most of the solid
  // green canopy visible from above instead of outlining eight white holes.
  const snowPatches = [
    { position: [-1.03, 2.33, -0.08] as VectorTuple, scale: [0.52, 0.1, 0.3] as VectorTuple, rotation: 0.12 },
    { position: [-0.17, 2.65, -0.13] as VectorTuple, scale: [0.6, 0.11, 0.34] as VectorTuple, rotation: -0.18 },
    { position: [0.72, 2.79, -0.2] as VectorTuple, scale: [0.52, 0.1, 0.29] as VectorTuple, rotation: 0.1 },
    { position: [1.66, 2.82, -0.21] as VectorTuple, scale: [0.45, 0.09, 0.26] as VectorTuple, rotation: -0.08 },
    { position: [0.71, 3.2, -0.51] as VectorTuple, scale: [0.36, 0.08, 0.22] as VectorTuple, rotation: 0.16 },
  ] as const;
  const snowPatchGeometry = new THREE.DodecahedronGeometry(1, 0);
  snowPatches.forEach((patch, index) => {
    addMesh(group, `krummholz-wind-snow-${index}`, snowPatchGeometry, timberSnow, {
      position: patch.position,
      scale: patch.scale,
      rotation: [0, patch.rotation, index % 2 === 0 ? -0.05 : 0.04],
    });
  });
  addMesh(group, 'krummholz-foot-drift', new THREE.IcosahedronGeometry(1, 1), timberSnow, {
    position: [-1.13, 0.09, 0.02],
    scale: [1.58, 0.28, 1.12],
  });
  return finishModel('krummholz', 'Wind-Sculpted Krummholz', 'timberline', group);
}

function distortedCairnStone(index: number): THREE.BufferGeometry {
  const geometry = new THREE.DodecahedronGeometry(1, 0);
  const positions = geometry.getAttribute('position') as THREE.BufferAttribute;
  for (let vertex = 0; vertex < positions.count; vertex += 1) {
    const x = positions.getX(vertex);
    const y = positions.getY(vertex);
    const z = positions.getZ(vertex);
    const offset = 0.91 + ((vertex * 7 + index * 5) % 9) * 0.022;
    positions.setXYZ(vertex, x * offset, y * (2.01 - offset), z * (0.96 + ((vertex + index) % 4) * 0.025));
  }
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function createRibbonScrapGeometry(length: number, width: number, bend: number, sag: number): THREE.BufferGeometry {
  const stations = [
    { x: 0, y: 0, z: 0, width: width * 0.5 },
    { x: length * 0.25, y: -sag * 0.08, z: bend * 0.12, width: width * 0.48 },
    { x: length * 0.52, y: sag * 0.06, z: -bend * 0.24, width: width * 0.42 },
    { x: length * 0.78, y: -sag * 0.34, z: bend * 0.52, width: width * 0.34 },
    { x: length, y: -sag, z: bend, width: width * 0.16 },
  ];
  const positions: number[] = [];
  const indices: number[] = [];
  stations.forEach((station, index) => {
    // Unequal edges give each scrap a torn, hand-cut hem.
    positions.push(
      station.x,
      station.y + station.width * (index % 2 === 0 ? 1 : 0.82),
      station.z,
      station.x,
      station.y - station.width * (index % 3 === 0 ? 0.72 : 1),
      station.z + (index % 2 === 0 ? 0.018 : -0.018),
    );
    if (index === 0) return;
    const previous = (index - 1) * 2;
    const current = index * 2;
    indices.push(previous, previous + 1, current, current, previous + 1, current + 1);
  });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

export function createRibbonCairnModel(): THREE.Group {
  const group = new THREE.Group();
  const stones = [
    { position: [-0.1, 0.42, 0.06] as VectorTuple, scale: [1.28, 0.48, 0.94] as VectorTuple, rotation: [0.04, 0.28, -0.08] as VectorTuple },
    { position: [0.17, 0.96, -0.12] as VectorTuple, scale: [1.03, 0.4, 0.76] as VectorTuple, rotation: [-0.1, -0.42, 0.12] as VectorTuple },
    { position: [-0.2, 1.39, 0.11] as VectorTuple, scale: [0.82, 0.34, 0.64] as VectorTuple, rotation: [0.13, 0.54, -0.09] as VectorTuple },
    { position: [0.12, 1.76, -0.08] as VectorTuple, scale: [0.67, 0.29, 0.51] as VectorTuple, rotation: [-0.08, -0.25, 0.16] as VectorTuple },
    { position: [-0.12, 2.07, 0.08] as VectorTuple, scale: [0.49, 0.24, 0.42] as VectorTuple, rotation: [0.17, 0.46, -0.12] as VectorTuple },
    { position: [0.08, 2.32, -0.04] as VectorTuple, scale: [0.33, 0.18, 0.3] as VectorTuple, rotation: [-0.13, -0.34, 0.08] as VectorTuple },
  ] as const;
  stones.forEach((stone, index) => {
    addMesh(group, `cairn-stone-${index}`, distortedCairnStone(index), index % 2 ? cairnShade : cairnStone, {
      position: stone.position,
      scale: stone.scale,
      rotation: stone.rotation,
    });
  });
  addBeam(group, 'cairn-ribbon-pole', [0.08, 2.26, -0.04], [0.2, 4.56, -0.12], 0.068, timberDark, 0.036, 5);
  addMesh(group, 'upper-coral-ribbon', createRibbonScrapGeometry(1.48, 0.22, 0.28, 0.18), ribbonCoral, {
    position: [0.18, 4.26, -0.11],
    rotation: [-0.06, 0.18, -0.04],
  });
  addMesh(group, 'middle-ochre-ribbon', createRibbonScrapGeometry(1.16, 0.17, -0.24, 0.13), ribbonOchre, {
    position: [0.15, 3.72, -0.09],
    rotation: [0.08, -0.34, 0.06],
  });
  addMesh(group, 'lower-coral-ribbon', createRibbonScrapGeometry(0.8, 0.13, 0.2, 0.11), ribbonCoral, {
    position: [0.12, 3.22, -0.07],
    rotation: [-0.04, 0.52, -0.05],
  });
  for (const [index, y] of [3.22, 3.72, 4.26].entries()) {
    addMesh(group, `cairn-ribbon-knot-${index}`, new THREE.TorusGeometry(0.076, 0.016, 4, 7), timberDark, {
      position: [0.12 + (y - 3.22) * 0.06, y, -0.07 - (y - 3.22) * 0.04],
      rotation: [Math.PI / 2, 0, 0],
    });
  }
  addMesh(group, 'cairn-foot-snow', new THREE.IcosahedronGeometry(1, 1), timberSnow, {
    position: [-0.06, 0.08, 0.02],
    scale: [1.65, 0.25, 1.2],
  });
  return finishModel('ribbon-cairn', 'Ribbon-Topped Trail Cairn', 'timberline', group);
}

export function createAvalancheFenceModel(): THREE.Group {
  const group = new THREE.Group();
  const postXs = [-3.65, 0, 3.65];
  postXs.forEach((x, index) => {
    addBeam(group, `fence-front-post-${index}`, [x, 0, 0.42], [x + 0.16, 3.62, 0], 0.21, timberDark, 0.17, 4);
    addBeam(group, `fence-rear-brace-${index}`, [x, 0, -1.24], [x + 0.05, 2.76, 0.05], 0.16, timber, 0.13, 4);
    addBeam(group, `fence-ground-brace-${index}`, [x, 0.1, -1.24], [x, 0.1, 0.42], 0.15, timber, 0.15, 4);
    addMesh(group, `fence-post-snow-cap-${index}`, new THREE.BoxGeometry(0.62, 0.16, 0.62), timberSnow, {
      position: [x + 0.16, 3.7, -0.02],
      rotation: [0, 0.1 * index, -0.04],
    });
  });
  for (const [index, y] of [0.92, 1.72, 2.52, 3.25].entries()) {
    addBeam(group, `fence-cross-rail-${index}`, [-4.05, y, 0.2], [4.05, y + 0.15, -0.12], 0.2, timber, 0.16, 4);
  }
  for (const [index, x] of [-2.28, 1.32].entries()) {
    addBeam(group, `fence-diagonal-${index}`, [x - 1.1, 0.52, 0.28], [x + 1.1, 3.38, -0.08], 0.13, timberDark, 0.11, 4);
  }
  addMesh(group, 'fence-top-snow-ledge', new THREE.BoxGeometry(8.55, 0.22, 0.72), timberSnow, {
    position: [0, 3.52, -0.05],
    rotation: [0.02, 0, 0.02],
  });
  for (const x of [-3.65, 0, 3.65]) {
    addMesh(group, `fence-foot-drift-${x}`, new THREE.IcosahedronGeometry(0.65, 1), timberSnow, {
      position: [x, 0.07, 0.15],
      scale: [1.3, 0.25, 1.05],
    });
  }
  return finishModel('avalanche-fence', 'Timber Avalanche Fence', 'timberline', group);
}

function createCup(group: THREE.Group, name: string, position: VectorTuple, mouthDirection: VectorTuple): void {
  addBeam(group, `${name}-arm`, [0, position[1], 0], position, 0.035, steel, 0.035, 5);
  const direction = new THREE.Vector3(...mouthDirection).normalize();
  const cup = addMesh(group, `${name}-bowl`, new THREE.CylinderGeometry(0.28, 0.13, 0.36, 7, 1, true), steel, {
    position,
  });
  cup.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);

  const backPosition: VectorTuple = [
    position[0] - direction.x * 0.18,
    position[1] - direction.y * 0.18,
    position[2] - direction.z * 0.18,
  ];
  const back = addMesh(group, `${name}-cup-back`, new THREE.CircleGeometry(0.13, 7), steel, {
    position: backPosition,
  });
  back.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), direction.clone().negate());

  const interiorPosition: VectorTuple = [
    position[0] - direction.x * 0.12,
    position[1] - direction.y * 0.12,
    position[2] - direction.z * 0.12,
  ];
  const interior = addMesh(group, `${name}-dark-interior`, new THREE.CircleGeometry(0.12, 7), steelShadow, {
    position: interiorPosition,
  });
  interior.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), direction);

  const rimPosition: VectorTuple = [
    position[0] + direction.x * 0.18,
    position[1] + direction.y * 0.18,
    position[2] + direction.z * 0.18,
  ];
  const rim = addMesh(group, `${name}-rolled-rim`, new THREE.TorusGeometry(0.28, 0.025, 4, 8), steel, {
    position: rimPosition,
  });
  rim.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), direction);
}

export function createWeatherMastModel(): THREE.Group {
  const group = new THREE.Group();
  addBeam(group, 'mast-main-pole', [0, 0, 0], [0, 5.7, 0], 0.12, steel, 0.08, 8);
  for (const [index, angle] of [0, (Math.PI * 2) / 3, (Math.PI * 4) / 3].entries()) {
    const foot: VectorTuple = [Math.cos(angle) * 1.35, 0, Math.sin(angle) * 1.35];
    addBeam(group, `mast-tripod-leg-${index}`, foot, [0, 3.35, 0], 0.11, steel, 0.08, 6);
    addMesh(group, `mast-foot-${index}`, new THREE.BoxGeometry(0.58, 0.16, 0.58), steel, {
      position: [foot[0], 0.08, foot[2]],
      rotation: [0, angle, 0],
    });
    addMesh(group, `mast-foot-bolt-${index}`, new THREE.CylinderGeometry(0.08, 0.08, 0.15, 6), steelShadow, {
      position: [foot[0], 0.2, foot[2]],
    });
    addMesh(group, `mast-foot-snow-${index}`, new THREE.IcosahedronGeometry(0.52, 1), timberSnow, {
      position: [foot[0], 0.05, foot[2]],
      scale: [1.2, 0.18, 0.9],
    });
  }
  addMesh(group, 'mast-tripod-apex-joint', new THREE.DodecahedronGeometry(0.22, 0), steelShadow, {
    position: [0, 3.35, 0],
    rotation: [0.2, 0.35, 0],
  });

  const rotorY = 5.08;
  createCup(group, 'east-wind-cup', [0.9, rotorY, 0], [0, 0, 1]);
  createCup(group, 'north-wind-cup', [0, rotorY, 0.9], [-1, 0, 0]);
  createCup(group, 'west-wind-cup', [-0.9, rotorY, 0], [0, 0, -1]);
  createCup(group, 'south-wind-cup', [0, rotorY, -0.9], [1, 0, 0]);
  addMesh(group, 'anemometer-rotor-hub', new THREE.CylinderGeometry(0.17, 0.17, 0.2, 7), steelShadow, {
    position: [0, rotorY, 0],
  });
  addMesh(group, 'anemometer-bearing-collar', new THREE.CylinderGeometry(0.2, 0.16, 0.12, 7), steel, {
    position: [0, rotorY - 0.15, 0],
  });

  const vaneY = 6.02;
  addBeam(group, 'wind-vane-pivot', [0, 5.25, 0], [0, vaneY + 0.12, 0], 0.048, steelShadow, 0.04, 6);
  addBeam(group, 'wind-vane-shaft', [-1.08, vaneY, 0], [1.14, vaneY, 0], 0.047, steel, 0.047, 6);
  addMesh(group, 'wind-vane-arrow-head', new THREE.ConeGeometry(0.22, 0.62, 6), ribbonCoral, {
    position: [1.38, vaneY, 0],
    rotation: [0, 0, -Math.PI / 2],
  });
  addMesh(group, 'wind-vane-arrow-tail', createTrapezoidGeometry(0.88, 0.22, 0.78), ribbonCoral, {
    position: [-0.18, vaneY, 0],
    rotation: [0, Math.PI, 0],
  });
  addMesh(group, 'wind-vane-pivot-joint', new THREE.DodecahedronGeometry(0.13, 0), steelShadow, {
    position: [0, vaneY, 0],
  });

  addBeam(group, 'instrument-box-mount', [0, 3.5, 0], [0.42, 3.5, 0], 0.06, steelShadow, 0.06, 5);
  addMesh(group, 'instrument-box-backplate', new THREE.BoxGeometry(0.12, 1.04, 0.76), steelShadow, {
    position: [0.4, 3.5, 0],
  });
  addMesh(group, 'weather-instrument-box', new THREE.BoxGeometry(0.72, 0.86, 0.62), timberSnow, {
    position: [0.78, 3.5, 0],
    rotation: [0, 0.04, 0],
  });
  addMesh(group, 'instrument-box-rain-cap', new THREE.BoxGeometry(0.86, 0.1, 0.76), steel, {
    position: [0.78, 3.98, 0],
    rotation: [0, 0.04, -0.025],
  });
  for (const y of [3.26, 3.48, 3.7]) {
    addMesh(group, `instrument-louver-${y}`, new THREE.BoxGeometry(0.64, 0.055, 0.1), steel, {
      position: [0.8, y, 0.34],
      rotation: [0.16, 0.04, 0],
    });
  }
  addBeam(group, 'instrument-cable', [0.4, 3.2, -0.15], [0.06, 2.82, -0.08], 0.025, steelShadow, 0.025, 5);
  return finishModel('weather-mast', 'Timberline Weather Mast', 'timberline', group);
}

function createRidgePackGeometry(): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute([
    -0.82, 0, 0.52,
    0.82, 0, 0.52,
    -0.82, 0, -0.44,
    0.82, 0, -0.44,
    -0.62, 1.5, 0.47,
    0.62, 1.5, 0.47,
    -0.62, 1.5, -0.38,
    0.62, 1.5, -0.38,
  ], 3));
  geometry.setIndex([
    0, 1, 5, 0, 5, 4,
    3, 2, 6, 3, 6, 7,
    2, 0, 4, 2, 4, 6,
    1, 3, 7, 1, 7, 5,
    4, 5, 7, 4, 7, 6,
    2, 3, 1, 2, 1, 0,
  ]);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function createRavenWingGeometry(side: -1 | 1): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute([
    0.38, 0.4, side * 0.24,
    -0.32, 0.35, side * 0.34,
    -0.72, -0.62, side * 0.25,
    0.2, -0.42, side * 0.34,
    -0.06, 0.04, side * 0.57,
  ], 3));
  geometry.setIndex(side > 0
    ? [0, 1, 4, 1, 2, 4, 2, 3, 4, 3, 0, 4]
    : [4, 1, 0, 4, 2, 1, 4, 3, 2, 4, 0, 3]);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

/** A bold ridge raven has adopted a snowbound pack and its brightest prize. */
export function createRavenCacheModel(): THREE.Group {
  const group = new THREE.Group();

  addMesh(group, 'abandoned-ridge-pack', createRidgePackGeometry(), packCanvas, {
    position: [-0.48, 0.18, 0],
    rotation: [0.035, -0.12, 0.075],
  });
  addMesh(group, 'pack-front-pocket', new THREE.BoxGeometry(0.95, 0.64, 0.16), packCanvasShadow, {
    position: [-0.38, 0.76, 0.53],
    rotation: [-0.04, -0.12, 0.04],
  });
  addMesh(group, 'pack-coral-flap', new THREE.BoxGeometry(1.14, 0.42, 0.12), ribbonCoral, {
    position: [-0.42, 1.28, 0.52],
    rotation: [-0.08, -0.12, 0.06],
  });
  addMesh(group, 'pack-brass-buckle', new THREE.BoxGeometry(0.2, 0.25, 0.08), ribbonOchre, {
    position: [-0.37, 1.06, 0.62],
    rotation: [-0.04, -0.12, 0.04],
  });
  addMesh(group, 'pack-side-pocket', new THREE.DodecahedronGeometry(0.38, 0), packCanvasShadow, {
    position: [-1.26, 0.88, 0.02],
    scale: [0.62, 1.08, 0.9],
    rotation: [0.04, -0.1, 0.08],
  });
  addMesh(group, 'pack-side-pocket-flap', new THREE.BoxGeometry(0.14, 0.24, 0.58), packCanvas, {
    position: [-1.47, 1.08, 0.02],
    rotation: [0.03, -0.1, 0.08],
  });

  // A raised grab loop remains visible from the plain rear view, where the
  // front pocket and bedroll are hidden by the body of the pack.
  addBeam(group, 'pack-carry-loop-left', [-0.83, 1.65, -0.35], [-0.73, 1.92, -0.42], 0.045, packCanvasShadow, 0.04, 5);
  addBeam(group, 'pack-carry-loop-top', [-0.73, 1.92, -0.42], [-0.35, 1.95, -0.42], 0.045, packCanvasShadow, 0.04, 5);
  addBeam(group, 'pack-carry-loop-right', [-0.35, 1.95, -0.42], [-0.24, 1.65, -0.35], 0.045, packCanvasShadow, 0.04, 5);

  addMesh(group, 'pack-bedroll', new THREE.CylinderGeometry(0.27, 0.27, 1.68, 8), ribbonOchre, {
    position: [-0.5, 0.35, 0.59],
    rotation: [0, 0, Math.PI / 2],
  });
  for (const [index, x] of [-0.88, -0.12].entries()) {
    addMesh(group, `bedroll-binding-${index}`, new THREE.CylinderGeometry(0.292, 0.292, 0.09, 8), timberDark, {
      position: [x, 0.35, 0.59],
      rotation: [0, 0, Math.PI / 2],
    });
  }
  for (const [index, x] of [-0.9, -0.04].entries()) {
    const upper: VectorTuple = [x + 0.05, 1.47, -0.39];
    const shoulder: VectorTuple = [x - 0.08, 0.92, -0.61];
    const lower: VectorTuple = [x + 0.03, 0.25, -0.43];
    addBeam(group, `pack-shoulder-strap-${index}-upper`, upper, shoulder, 0.052, packCanvasShadow, 0.052, 5);
    addBeam(group, `pack-shoulder-strap-${index}-lower`, shoulder, lower, 0.052, packCanvasShadow, 0.04, 5);
  }
  addMesh(group, 'pack-wind-snow', new THREE.IcosahedronGeometry(0.72, 1), timberSnow, {
    position: [-0.58, 1.65, -0.03],
    scale: [1.2, 0.23, 0.68],
    rotation: [0.03, -0.15, -0.04],
  });

  // The raven faces across the ridge, creating a head-to-tail axis that stays
  // readable against the upright mass of the pack.
  addMesh(group, 'raven-body', new THREE.DodecahedronGeometry(0.7, 0), ravenBlack, {
    position: [0.18, 2.25, -0.03],
    scale: [0.76, 1.05, 0.69],
    rotation: [0, 0, -0.18],
  });
  addMesh(group, 'raven-blue-chest', new THREE.DodecahedronGeometry(0.43, 0), ravenBlue, {
    position: [0.54, 2.45, -0.02],
    scale: [0.84, 1.12, 0.78],
    rotation: [0, 0, -0.18],
  });
  addMesh(group, 'raven-head', new THREE.DodecahedronGeometry(0.42, 0), ravenBlack, {
    position: [0.61, 2.96, -0.01],
    scale: [1.04, 0.94, 0.92],
    rotation: [0, 0, -0.08],
  });
  addMesh(group, 'raven-beak', new THREE.ConeGeometry(0.18, 0.68, 5), ravenBeak, {
    position: [1.11, 2.92, -0.01],
    rotation: [0, 0, -Math.PI / 2],
    scale: [0.84, 1, 0.7],
  });
  for (const [index, z] of [-0.35, 0.33].entries()) {
    addMesh(group, `raven-amber-eye-${index}`, new THREE.DodecahedronGeometry(0.052, 0), ravenEye, {
      position: [0.79, 3.05, z],
    });
  }
  for (const side of [-1, 1] as const) {
    addMesh(group, `raven-folded-wing-${side}`, createRavenWingGeometry(side), ravenBlue, {
      position: [0.1, 2.3, 0],
      rotation: [0, 0, -0.12],
    });
  }
  for (const [index, z] of [-0.2, 0, 0.2].entries()) {
    addBeam(
      group,
      `raven-tail-feather-${index}`,
      [-0.16, 1.95, z * 0.72],
      [-1.08 - Math.abs(z) * 0.35, 1.42 - Math.abs(z) * 0.12, z],
      0.15,
      index === 1 ? ravenBlue : ravenBlack,
      0.018,
      4,
    );
  }
  for (const [index, z] of [-0.17, 0.17].entries()) {
    addBeam(group, `raven-leg-${index}`, [0.13, 1.86, z], [0.04, 1.62, z], 0.033, ravenBeak, 0.026, 5);
    addBeam(group, `raven-forward-talon-${index}`, [0.04, 1.62, z], [0.29, 1.58, z], 0.023, ravenBeak, 0.012, 4);
    addBeam(group, `raven-back-talon-${index}`, [0.04, 1.62, z], [-0.16, 1.58, z], 0.022, ravenBeak, 0.01, 4);
  }

  // A bright wool mitten makes the raven's opportunistic theft readable even
  // when the charcoal plumage is seen against the shadow side of the pack.
  addBeam(group, 'mitten-cuff-in-beak', [1.34, 2.9, 0], [1.46, 2.57, 0.02], 0.035, ribbonCoral, 0.028, 5);
  addMesh(group, 'stolen-mitten-cuff', new THREE.BoxGeometry(0.34, 0.22, 0.24), ribbonCoral, {
    position: [1.48, 2.48, 0.02],
    rotation: [0.08, 0, -0.14],
  });
  addMesh(group, 'stolen-mitten-palm', new THREE.DodecahedronGeometry(0.29, 0), ribbonCoral, {
    position: [1.54, 2.2, 0.02],
    scale: [0.78, 1.08, 0.62],
    rotation: [0.1, 0, -0.12],
  });
  addMesh(group, 'stolen-mitten-thumb', new THREE.DodecahedronGeometry(0.14, 0), ribbonCoral, {
    position: [1.76, 2.25, 0.03],
    scale: [1.05, 0.7, 0.62],
    rotation: [0, 0, -0.42],
  });

  addMesh(group, 'raven-cache-foot-drift', new THREE.IcosahedronGeometry(1, 1), timberSnow, {
    position: [-0.05, 0.12, 0.02],
    scale: [2.18, 0.3, 1.28],
    rotation: [0, -0.08, 0],
  });
  return finishModel('raven-cache', 'Raven-Raided Ridge Pack', 'timberline', group);
}

function addPtarmiganTail(
  group: THREE.Group,
  name: string,
  root: VectorTuple,
  direction: VectorTuple,
  spread: number,
): void {
  for (const [index, offset] of [-1, 0, 1].entries()) {
    const end: VectorTuple = [
      root[0] + direction[0],
      root[1] + direction[1] + (index === 1 ? 0.05 : -0.03),
      root[2] + direction[2] + offset * spread,
    ];
    const join: VectorTuple = [
      root[0] + direction[0] * 0.62,
      root[1] + direction[1] * 0.62,
      root[2] + direction[2] * 0.62 + offset * spread * 0.62,
    ];
    addBeam(group, `${name}-white-tail-feather-${index}`, root, join, 0.15, ptarmiganWhite, 0.12, 4);
    addBeam(group, `${name}-black-tail-tip-${index}`, join, end, 0.125, ptarmiganCharcoal, 0.025, 4);
  }
}

function addPtarmiganFace(
  group: THREE.Group,
  name: string,
  head: VectorTuple,
  beakEnd: VectorTuple,
  nearZ: number,
): void {
  addMesh(group, `${name}-head`, new THREE.DodecahedronGeometry(0.34, 0), ptarmiganWhite, {
    position: head,
    scale: [1, 1.06, 0.92],
  });
  addBeam(group, `${name}-beak`, [head[0] + 0.2, head[1] - 0.02, head[2]], beakEnd, 0.1, ptarmiganCharcoal, 0.015, 5);
  addMesh(group, `${name}-near-eye`, new THREE.DodecahedronGeometry(0.057, 0), ptarmiganCharcoal, {
    position: [head[0] + 0.16, head[1] + 0.1, nearZ],
  });
  addMesh(group, `${name}-red-eyebrow-comb`, new THREE.IcosahedronGeometry(0.11, 0), ptarmiganComb, {
    position: [head[0] + 0.13, head[1] + 0.2, nearZ + (nearZ >= head[2] ? 0.015 : -0.015)],
    scale: [1.3, 0.55, 0.42],
    rotation: [0, 0, 0.1],
  });
}

/** Two winter birds freeze an exuberant powder bath and its wary lookout. */
export function createPowderBathingPtarmiganPairModel(): THREE.Group {
  const group = new THREE.Group();

  // A cool hollow and broken collar establish the behavior without becoming
  // a large display plinth that would shrink the birds in the field guide.
  addMesh(group, 'ptarmigan-powder-bath-hollow', new THREE.CircleGeometry(1.05, 9), powderBathBlue, {
    position: [0.55, 0.018, 0.22],
    rotation: [-Math.PI / 2, 0, 0],
    scale: [1.22, 0.76, 1],
  });
  for (const [index, collar] of [
    { x: -0.34, z: 0.75, sx: 0.62, sz: 0.34 },
    { x: 0.35, z: 0.92, sx: 0.58, sz: 0.3 },
    { x: 1.05, z: 0.8, sx: 0.54, sz: 0.31 },
    { x: 1.55, z: 0.35, sx: 0.42, sz: 0.3 },
    { x: 1.35, z: -0.42, sx: 0.5, sz: 0.28 },
  ].entries()) {
    addMesh(group, `ptarmigan-bath-collar-${index}`, new THREE.IcosahedronGeometry(0.5, 0), timberSnow, {
      position: [collar.x, 0.08, collar.z],
      scale: [collar.sx, 0.25, collar.sz],
      rotation: [0, index * 0.57, index % 2 ? 0.04 : -0.04],
    });
  }

  // The active bird is rolled toward the viewer, alert head raised, with one
  // wing buried and one wing fanning through the air.
  addMesh(group, 'bathing-ptarmigan-body', new THREE.IcosahedronGeometry(0.78, 1), ptarmiganWhite, {
    position: [0.55, 0.5, 0.34],
    scale: [1.18, 0.7, 0.82],
    rotation: [0.24, -0.04, -0.48],
  });
  addMesh(group, 'bathing-ptarmigan-belly-shadow', new THREE.IcosahedronGeometry(0.58, 0), ptarmiganShadow, {
    position: [0.48, 0.34, 0.4],
    scale: [1.18, 0.38, 0.72],
    rotation: [0.18, 0, -0.22],
  });
  addPtarmiganFace(group, 'bathing-ptarmigan', [1.28, 0.92, 0.4], [1.65, 0.88, 0.4], 0.69);

  const raisedWingFeathers = [
    { position: [0.38, 1.02, -0.25] as VectorTuple, scale: [0.82, 0.22, 0.32] as VectorTuple, rotation: -0.58 },
    { position: [0.12, 1.32, -0.39] as VectorTuple, scale: [0.9, 0.21, 0.29] as VectorTuple, rotation: -0.84 },
    { position: [-0.08, 1.56, -0.49] as VectorTuple, scale: [0.8, 0.19, 0.26] as VectorTuple, rotation: -1.08 },
  ];
  raisedWingFeathers.forEach((feather, index) => {
    addMesh(group, `bathing-raised-wing-feather-${index}`, new THREE.DodecahedronGeometry(0.7, 0), index === 2 ? ptarmiganUnderwing : ptarmiganShadow, {
      position: feather.position,
      scale: feather.scale,
      rotation: [0.18, -0.16, feather.rotation],
    });
  });
  addMesh(group, 'bathing-buried-wing', new THREE.DodecahedronGeometry(0.65, 0), ptarmiganShadow, {
    position: [0.54, 0.3, 0.78],
    scale: [1.05, 0.26, 0.58],
    rotation: [0.08, -0.12, -0.14],
  });
  addPtarmiganTail(group, 'bathing-ptarmigan', [-0.12, 0.48, 0.21], [-0.78, 0.08, 0], 0.22);

  addBeam(group, 'bathing-kicking-leg', [0.06, 0.47, -0.02], [-0.25, 0.76, -0.52], 0.115, ptarmiganFeet, 0.08, 5);
  for (const [index, toeZ] of [-0.12, 0.02, 0.15].entries()) {
    addBeam(group, `bathing-kicking-toe-${index}`, [-0.25, 0.76, -0.52], [-0.52, 0.78, -0.52 + toeZ], 0.04, ptarmiganFeet, 0.014, 4);
  }

  // The lookout is taller, diagonally separated, and faces across the ridge.
  addMesh(group, 'lookout-ptarmigan-body', new THREE.IcosahedronGeometry(0.72, 1), ptarmiganWhite, {
    position: [-1.15, 0.8, -0.57],
    scale: [0.78, 1.12, 0.78],
    rotation: [0.03, -0.42, 0.06],
  });
  addMesh(group, 'lookout-ptarmigan-near-wing', new THREE.DodecahedronGeometry(0.54, 0), ptarmiganShadow, {
    position: [-1.02, 0.83, -0.93],
    scale: [0.95, 1.02, 0.38],
    rotation: [0.04, -0.35, -0.11],
  });
  addMesh(group, 'lookout-ptarmigan-far-wing', new THREE.DodecahedronGeometry(0.5, 0), ptarmiganUnderwing, {
    position: [-1.34, 0.82, -0.28],
    scale: [0.92, 0.98, 0.32],
    rotation: [-0.04, -0.35, 0.1],
  });
  addPtarmiganFace(group, 'lookout-ptarmigan', [-0.88, 1.55, -0.58], [-0.54, 1.53, -0.75], -0.9);
  addPtarmiganTail(group, 'lookout-ptarmigan', [-1.56, 0.62, -0.43], [-0.62, -0.03, 0.1], 0.18);
  for (const [index, z] of [-0.74, -0.43].entries()) {
    addBeam(group, `lookout-feathered-ankle-${index}`, [-1.14 + index * 0.12, 0.35, z], [-1.12 + index * 0.12, 0.09, z], 0.1, ptarmiganWhite, 0.07, 5);
    for (const [toeIndex, toeZ] of [-0.09, 0.09].entries()) {
      addBeam(group, `lookout-toe-${index}-${toeIndex}`, [-1.12 + index * 0.12, 0.09, z], [-0.82 + index * 0.12, 0.06, z + toeZ], 0.038, ptarmiganFeet, 0.012, 4);
    }
  }

  // A close powder crown follows the raised wing. It stays horizontally
  // inside the birds, preserving their phone-scale gallery framing.
  for (const [index, puff] of [
    { position: [-0.2, 1.1, -0.58] as VectorTuple, scale: [0.2, 0.15, 0.17] as VectorTuple },
    { position: [-0.02, 1.38, -0.52] as VectorTuple, scale: [0.17, 0.13, 0.15] as VectorTuple },
    { position: [0.22, 1.62, -0.38] as VectorTuple, scale: [0.15, 0.12, 0.14] as VectorTuple },
    { position: [0.5, 1.52, -0.18] as VectorTuple, scale: [0.13, 0.1, 0.12] as VectorTuple },
  ].entries()) {
    addMesh(group, `ptarmigan-powder-puff-${index}`, new THREE.OctahedronGeometry(1, 0), timberSnow, {
      position: puff.position,
      scale: puff.scale,
      rotation: [index * 0.23, index * 0.41, index % 2 ? 0.16 : -0.14],
    });
  }

  return finishModel('powder-bathing-ptarmigan-pair', 'Powder-Bathing Ptarmigan Pair', 'timberline', group);
}
